import React, { useEffect, useState } from 'react';
import * as adminApi from '../../../../api/adminApi';
import modalStyles from './AdminModals.module.css';

interface EditAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
  admin: adminApi.AdminSummary | null;
  /** The signed-in admin: their own account cannot be deactivated from here. */
  currentAdminId?: number | null;
  onSave: () => void;
  onStatusChange?: (adminId: string, isActive: boolean) => void;
}

/**
 * Admin accounts are local (name, email, password in this database) since the Cognito
 * removal, so name, email and role are editable here. The status button deactivates or
 * reactivates; it is hidden for your own account. Refusals show the server's message.
 */
export const EditAdminModal: React.FC<EditAdminModalProps> = ({
  isOpen,
  onClose,
  token,
  admin,
  currentAdminId,
  onSave,
  onStatusChange,
}) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<string>('');
  const [roles, setRoles] = useState<adminApi.AdminCustomRole[]>([]);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!admin) return;
    setFullName(admin.full_name || '');
    setEmail(admin.email || '');
    setRoleId(admin.custom_role_id ? String(admin.custom_role_id) : '');
    setError(null);
  }, [admin]);

  useEffect(() => {
    if (!isOpen || !token) return;
    let cancelled = false;
    adminApi.getAdminCustomRoles(token)
      .then((list) => { if (!cancelled) setRoles(list); })
      .catch(() => { if (!cancelled) setRoles([]); });
    return () => { cancelled = true; };
  }, [isOpen, token]);

  if (!isOpen || !admin) return null;

  const isSelf = currentAdminId != null && admin.id === currentAdminId;

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setError(null);
    try {
      const payload: adminApi.AdminUpdatePayload = {};
      if (fullName.trim() !== (admin.full_name || '')) payload.full_name = fullName.trim();
      if (email.trim().toLowerCase() !== (admin.email || '').toLowerCase()) payload.email = email.trim();
      const nextRole = roleId ? Number(roleId) : 0;   // 0 = default full access
      if (nextRole !== (admin.custom_role_id || 0)) payload.custom_role_id = nextRole;
      if (Object.keys(payload).length > 0) await adminApi.updateAdmin(token, String(admin.id), payload);
      onSave();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!token) return;
    setUpdating(true);
    setError(null);
    try {
      await adminApi.updateAdminStatus(token, String(admin.id), !admin.is_active);
      onStatusChange?.(String(admin.id), !admin.is_active);
      onSave();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className={modalStyles.overlay} onClick={onClose}>
      <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.header}>
          <h2 className={modalStyles.title}>Edit Admin</h2>
          <button onClick={onClose} className={modalStyles.closeBtn} aria-label="Close">✕</button>
        </div>

        <div className={modalStyles.body}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ds-text)' }}>Full Name</span>
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className={modalStyles.input} />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ds-text)' }}>Email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={modalStyles.input} />
              <span style={{ fontSize: '0.8rem', color: 'var(--ds-text-muted)' }}>This is the sign-in address; password resets go here.</span>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ds-text)' }}>Assigned Role</span>
              <select value={roleId} onChange={(e) => setRoleId(e.target.value)} className={modalStyles.input} disabled={isSelf}>
                <option value="">Admin (default full access)</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
              {isSelf && <span style={{ fontSize: '0.8rem', color: 'var(--ds-text-muted)' }}>You cannot change your own role.</span>}
            </label>

            <div className={modalStyles.statBox}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: 'var(--ds-text)' }}>Account Status</p>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: 'var(--ds-text-muted)' }}>
                    {admin.is_active ? 'Currently active' : 'Currently inactive — cannot sign in'}
                  </p>
                </div>
                {isSelf ? (
                  <span style={{ fontSize: '0.8rem', color: 'var(--ds-text-muted)' }}>Your own account</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleStatusChange}
                    disabled={updating}
                    style={{
                      padding: '0.5rem 1rem',
                      background: admin.is_active ? 'var(--ds-danger)' : 'var(--ds-success)',
                      color: 'var(--ds-text-on-dark)',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      cursor: updating ? 'not-allowed' : 'pointer',
                      opacity: updating ? 0.6 : 1,
                    }}
                  >
                    {updating ? 'Updating...' : (admin.is_active ? 'Deactivate' : 'Activate')}
                  </button>
                )}
              </div>
            </div>

            {error && <p role="alert" style={{ margin: 0, color: 'var(--ds-danger)', fontSize: '0.9rem' }}>{error}</p>}
          </div>
        </div>

        <div className={modalStyles.footer}>
          <div className={modalStyles.actions}>
            <button type="button" onClick={handleSave} disabled={saving} className={`${modalStyles.btn} ${modalStyles.primary}`}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button type="button" onClick={onClose} disabled={saving} className={`${modalStyles.btn} ${modalStyles.neutral}`}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};
